import { forwardRef, useImperativeHandle, useRef } from "react"
import { useEditor, EditorContent, Extension } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import { Markdown, type MarkdownStorage } from "tiptap-markdown"
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Strikethrough,
  Code as CodeIcon,
  Link as LinkIcon,
  List as ListIcon,
  ListOrdered,
} from "lucide-react"
import { cn } from "@/lib/cn"
import type { Editor } from "@tiptap/react"

// WYSIWYG editor wired to markdown serialization. Operator types rich
// text; we serialize to markdown on submit so the stored body stays as
// portable markdown for the client SDK consumer.
//
// The editor is uncontrolled — parents read the current markdown via the
// imperative handle (getMarkdown / clear / focus). Parents that need to
// react to typing (e.g. enable/disable the send button) get notified
// via onChange with the current markdown string.

export type MarkdownEditorHandle = {
  getMarkdown: () => string
  clear:       () => void
  focus:       () => void
  /** Insert a string at the current cursor position. Used by the
   *  emoji picker so picked emoji land where the operator was typing. */
  insertText:  (text: string) => void
}

type Props = {
  initialContent?: string
  placeholder?:    string
  disabled?:       boolean
  toneClass?:      "reply" | "note"
  onChange?:       (markdown: string) => void
  onSubmit?:       () => void
}

// Custom extension that intercepts plain Enter to fire onSubmit, while
// letting Shift+Enter, lists, and code blocks behave naturally.
const SubmitOnEnter = Extension.create<{ onSubmit: () => void }>({
  name: "submitOnEnter",
  addOptions() {
    return { onSubmit: () => {} }
  },
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        // In lists / code blocks Enter has its own meaning; let the
        // default keymap handle it.
        if (
          this.editor.isActive("listItem") ||
          this.editor.isActive("codeBlock") ||
          this.editor.isActive("blockquote")
        ) {
          return false
        }
        this.options.onSubmit()
        return true
      },
    }
  },
})

export const MarkdownEditor = forwardRef<MarkdownEditorHandle, Props>(
  function MarkdownEditor(
    { initialContent = "", placeholder = "", disabled = false, toneClass = "reply", onChange, onSubmit },
    ref,
  ) {
    // Stash the latest onSubmit in a ref so the SubmitOnEnter extension
    // (configured once at editor creation) always sees the current
    // closure rather than a stale one.
    const onSubmitRef = useRef(onSubmit)
    onSubmitRef.current = onSubmit

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading:        false,
          horizontalRule: false,
          // Bring our own Link extension below — StarterKit v3 ships
          // one with default styling, but we want the brand-red color
          // + autolink behavior, so disable the built-in to avoid the
          // "Duplicate extension names" warning.
          link: false,
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          HTMLAttributes: {
            class: "text-brand-red underline-offset-2 hover:underline",
            rel:   "noopener noreferrer",
            target:"_blank",
          },
        }),
        Placeholder.configure({ placeholder }),
        Markdown.configure({
          html:                  false,
          tightLists:            true,
          bulletListMarker:      "-",
          linkify:               true,
          breaks:                true,
          transformPastedText:   true,
          transformCopiedText:   true,
        }),
        SubmitOnEnter.configure({
          onSubmit: () => onSubmitRef.current?.(),
        }),
      ],
      content: initialContent,
      editable: !disabled,
      onUpdate({ editor }) {
        const md = (editor.storage as unknown as { markdown: MarkdownStorage }).markdown
        onChange?.(md.getMarkdown())
      },
      editorProps: {
        attributes: {
          class: cn(
            "tiptap-prosemirror min-h-24 text-sm text-white outline-none",
            "leading-tight",
          ),
        },
      },
    })

    useImperativeHandle(ref, () => ({
      getMarkdown: () => {
        if (!editor) return ""
        const md = (editor.storage as unknown as { markdown: MarkdownStorage }).markdown
        return md.getMarkdown()
      },
      clear:      () => editor?.commands.clearContent(true),
      focus:      () => editor?.commands.focus(),
      insertText: (text: string) => editor?.chain().focus().insertContent(text).run(),
    }), [editor])

    if (!editor) {
      // First render — Tiptap async-initializes. Reserve the space so
      // the composer doesn't jump.
      return <div className="min-h-24" />
    }

    return (
      <div className={cn("tiptap-editor", toneClass === "note" && "tiptap-note")}>
        <FormatToolbar editor={editor} disabled={disabled} />
        <EditorContent editor={editor} />
      </div>
    )
  },
)

function FormatToolbar({ editor, disabled }: { editor: Editor; disabled: boolean }) {
  return (
    <div className="mb-2 flex items-center gap-0.5 border-b border-white/5 pb-2">
      <ToolButton
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={disabled}
        title="Bold (⌘B)"
      ><BoldIcon size={14} strokeWidth={2.25} /></ToolButton>
      <ToolButton
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={disabled}
        title="Italic (⌘I)"
      ><ItalicIcon size={14} strokeWidth={2.25} /></ToolButton>
      <ToolButton
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={disabled}
        title="Strikethrough"
      ><Strikethrough size={14} strokeWidth={2.25} /></ToolButton>
      <ToolButton
        active={editor.isActive("code")}
        onClick={() => editor.chain().focus().toggleCode().run()}
        disabled={disabled}
        title="Inline code"
      ><CodeIcon size={14} strokeWidth={2.25} /></ToolButton>
      <span className="mx-1 h-4 w-px bg-white/10" />
      <ToolButton
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        disabled={disabled}
        title="Bulleted list"
      ><ListIcon size={14} strokeWidth={2.25} /></ToolButton>
      <ToolButton
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={disabled}
        title="Numbered list"
      ><ListOrdered size={14} strokeWidth={2.25} /></ToolButton>
      <span className="mx-1 h-4 w-px bg-white/10" />
      <ToolButton
        active={editor.isActive("link")}
        onClick={() => promptForLink(editor)}
        disabled={disabled}
        title="Link"
      ><LinkIcon size={14} strokeWidth={2.25} /></ToolButton>
    </div>
  )
}

function ToolButton({ active, onClick, disabled, title, children }: {
  active:    boolean
  onClick:   () => void
  disabled?: boolean
  title:     string
  children:  React.ReactNode
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault() /* keep editor focus */ }}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-md transition disabled:opacity-40",
        active
          ? "bg-white/10 text-white"
          : "text-app-muted hover:bg-white/5 hover:text-white",
      )}
    >
      {children}
    </button>
  )
}

// Minimalist link prompt. If text is selected, wraps it; if a link is
// already active and the prompt is cleared, removes the link.
function promptForLink(editor: Editor) {
  const previous = editor.getAttributes("link").href as string | undefined
  const url = window.prompt("URL", previous ?? "https://")
  if (url === null) return
  if (url === "") {
    editor.chain().focus().extendMarkRange("link").unsetLink().run()
    return
  }
  editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
}
